import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Vedic-styled hover tooltip — appears on the side of a card with rich detail.
 * Used for Navagraha + Nakshatra teach-tooltips on the Landing page.
 *
 * Props:
 *  - children: trigger element (the card)
 *  - content:  React node rendered inside the popover
 *  - placement: 'top' | 'bottom' | 'auto'  (auto picks based on viewport)
 */
const HoverTooltip = ({ children, content, placement = "auto", className = "" }) => {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState("bottom"); // resolved placement
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [maxH, setMaxH] = useState(560);
  const closeTimer = useRef(null);

  const computePosition = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const TOOLTIP_H_ESTIMATE = 540;
    const VIEWPORT_PAD = 20;
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;

    let want;
    if (placement === "top") {
      want = spaceAbove >= 260 ? "top" : "bottom";
    } else if (placement === "bottom") {
      want = spaceBelow >= 260 ? "bottom" : "top";
    } else {
      // auto: pick whichever side has more room — only fall back to undersized side if neither has 260
      want = spaceBelow >= spaceAbove ? "bottom" : "top";
    }
    setPos(want);

    // Compute available height on chosen side and clamp tooltip
    const availH = want === "bottom" ? spaceBelow - VIEWPORT_PAD : spaceAbove - VIEWPORT_PAD;
    setMaxH(Math.max(220, Math.min(TOOLTIP_H_ESTIMATE, availH)));

    // Centre horizontally on the trigger; clamp inside viewport with 16px margin.
    // NOTE: the popover renders at w-[480px] with max-w:calc(100vw-32px); we
    // measure against the same 480 value here so centering matches the actual
    // rendered width (bug fix Jul 21 2026 — was 380 which drifted the popover
    // ~50px off-centre and made the caret miss the card on desktop).
    const popoverW = Math.min(480, window.innerWidth - 32);
    let left = r.left + r.width / 2 - popoverW / 2;
    if (left < 16) left = 16;
    if (left + popoverW > window.innerWidth - 16) left = window.innerWidth - popoverW - 16;
    setCoords({
      top: want === "bottom" ? r.bottom + 12 : r.top - 12,
      left,
    });
  };

  const show = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    computePosition();
    setOpen(true);
    // Recompute on next two frames to catch any layout shifts (scroll, reveal animations)
    requestAnimationFrame(() => {
      computePosition();
      requestAnimationFrame(computePosition);
    });
  };

  const scheduleHide = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 90);
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => computePosition();
    // Tap-outside-to-close for mobile touch users (added Jul 21 2026 alongside
    // the onClick tap-to-open handler below). Guarded by wrapRef so a tap on
    // the trigger itself doesn't immediately re-close.
    const onDocPointer = (e) => {
      if (!wrapRef.current) return;
      if (wrapRef.current.contains(e.target)) return;
      // If the click landed inside the tooltip popover (portaled to body),
      // don't close either — let the user interact with the popover content.
      const popover = document.querySelector('[data-testid="hover-tooltip-popover"]');
      if (popover && popover.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    document.addEventListener("pointerdown", onDocPointer);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("pointerdown", onDocPointer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Tap-to-open for touch devices. On desktop, click also works (open persists
  // instead of relying on hover) — arguably better UX everywhere.
  const toggle = (e) => {
    e.stopPropagation();
    setOpen((v) => {
      if (!v) {
        computePosition();
        requestAnimationFrame(() => {
          computePosition();
          requestAnimationFrame(computePosition);
        });
      }
      return !v;
    });
  };

  return (
    <>
      <div
        ref={wrapRef}
        className={className}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={scheduleHide}
        onClick={toggle}
        tabIndex={0}
      >
        {children}
      </div>
      {open && createPortal(
        <div
          className="fixed z-[80] w-[480px] max-w-[calc(100vw-32px)] pointer-events-auto"
          style={{
            top: coords.top,
            left: coords.left,
            transform: pos === "top" ? "translateY(-100%)" : "none",
          }}
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
          role="tooltip"
          data-testid="hover-tooltip-popover"
        >
          <div className="animate-tooltip-rise relative">
            {/* Optional caret */}
            <div
              className="absolute w-2 h-2 rotate-45 bg-cream-soft border-l border-t border-[var(--hairline-gold)]"
              style={{
                left: "calc(50% - 4px)",
                [pos === "bottom" ? "top" : "bottom"]: -5,
                transform: pos === "bottom" ? "rotate(45deg)" : "rotate(225deg)",
              }}
            />
            <div
              className="relative bg-cream-soft border border-[var(--hairline-gold)] shadow-[0_18px_60px_-12px_rgba(46,26,14,0.35)] overflow-y-auto tooltip-scroll"
              style={{ maxHeight: maxH }}
            >
              {content}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default HoverTooltip;
